import type { JSX } from 'react';
import { Link } from 'react-router';
import {
  Rocket,
  Wand2,
  BookOpen,
  Layers,
  Newspaper,
  Scissors,
  LayoutGrid,
  Camera,
  Megaphone,
  Shapes,
  Gamepad2,
  AppWindow,
  Coins,
  Brush,
  Images,
  Server,
  GitCompare,
  Terminal,
  Network,
  Sparkles,
  ShieldCheck,
  Scale,
  type LucideIcon,
} from 'lucide-react';
import { resolveAssetUrl } from './article/image-source.js';
import type { BlogCategory, BlogMeta } from './types.js';

// Shared blog card config + the default post thumbnail card. Used by the blog
// index showcase grids and the article "Keep reading" rail, so a single edit
// restyles every post thumbnail across the app.

// Docs-style gradients (lifted from the /docs cards). Also exported for the
// index's core cards / news hero fallback.
export const GRADIENT = {
  lime: 'linear-gradient(135deg, #c8f050, #a8e060, #DDFC74)',
  cyan: 'linear-gradient(135deg, #1abfdf, #2de1fc, #80f0ff)',
  purple: 'linear-gradient(135deg, #8040c0, #a060e0, #c090f0)',
} as const;

// Representative image (and optional video) per post for its card thumbnail.
// A post left out here falls back to a gradient panel (e.g. the install guide).
export const CARD_MEDIA: Record<string, { image?: string; video?: string }> = {
  'gpt-image-2-for-ai-agents': { image: 'images/hero-bee-lavender.png' },
  'transparent-background-png-generator': { image: 'images/bg-removal-before-cloud.png' },
  'ai-pixel-art-generator': { image: 'images/pixel-warrior.png' },
  'ai-product-photography-no-studio': { image: 'images/product-tea-hero.png' },
  'ai-marketing-creatives-at-scale': { image: 'images/marketing-mascot.png' },
  'ai-flat-illustrations': { image: 'images/flat-hero.png' },
  '2d-game-asset-generation': { image: 'images/game-dungeon-room.png' },
  'generate-app-icons-claude': { image: 'images/app-icon-pulse.png' },
  'agentbrush-prompting-guide': { image: 'images/prompt-dragon-village.png' },
  'ai-image-generation-cost-tokens': { image: 'images/quality-crystal-high.png' },
  'mask-editor-inpaint-iterate': {
    image: 'images/mask-editor-3-result.png',
    video: 'images/mask-editor-demo.mp4',
  },
  'generate-images-from-reference-images': { image: 'images/ref-robot-rooftop.png' },
  'best-ai-transparent-png-tools': { image: 'images/bg-removal-before.png' },
  'best-ai-game-asset-generators': { image: 'images/game-gen-iso-robot.png' },
  'generate-images-claude-cursor': { image: 'images/cursor-lightning-icon.png' },
  'vibe-coding-visual-assets': { image: 'images/vibe-hero-relay.png' },
  // install-agentbrush-claude-cursor + the text-only comparison/foundations
  // posts are intentionally absent -> gradient + icon fallback.
};

// Gradient + icon fallback when a post has no thumbnail image.
const CATEGORY_FALLBACK: Record<BlogCategory, { gradient: string; Icon: LucideIcon }> = {
  'use-case': { gradient: GRADIENT.lime, Icon: Layers },
  learning: { gradient: GRADIENT.cyan, Icon: BookOpen },
  comparison: { gradient: GRADIENT.purple, Icon: Layers },
  foundations: { gradient: GRADIENT.purple, Icon: BookOpen },
  news: { gradient: GRADIENT.cyan, Icon: Newspaper },
};

// Per-article icon shown centered over the (blurred) thumbnail.
const ICON_BY_SLUG: Record<string, LucideIcon> = {
  'transparent-background-png-generator': Scissors,
  'ai-pixel-art-generator': LayoutGrid,
  'ai-product-photography-no-studio': Camera,
  'ai-marketing-creatives-at-scale': Megaphone,
  'ai-flat-illustrations': Shapes,
  '2d-game-asset-generation': Gamepad2,
  'generate-app-icons-claude': AppWindow,
  'agentbrush-prompting-guide': Wand2,
  'ai-image-generation-cost-tokens': Coins,
  'mask-editor-inpaint-iterate': Brush,
  'install-agentbrush-claude-cursor': Rocket,
  'generate-images-from-reference-images': Images,
  'best-image-generation-mcp-servers': Server,
  'best-ai-game-asset-generators': Gamepad2,
  'agentbrush-vs-scenario': GitCompare,
  'best-ai-transparent-png-tools': Scissors,
  'generate-images-claude-cursor': Terminal,
  'what-is-model-context-protocol': Network,
  'vibe-coding-visual-assets': Sparkles,
  'ai-game-assets-steam-itch': ShieldCheck,
  'can-you-sell-ai-generated-images': Scale,
};

type CardVisual = {
  image?: string | undefined;
  gradient: string;
  Icon: LucideIcon;
};

export function getCardVisual(post: BlogMeta): CardVisual {
  const media = CARD_MEDIA[post.slug];
  const fallback = CATEGORY_FALLBACK[post.category];
  return {
    image: media?.image,
    gradient: fallback.gradient,
    Icon: ICON_BY_SLUG[post.slug] ?? fallback.Icon,
  };
}

// The default post thumbnail: the article image behind a blur + animated noise,
// with a centered icon, and the title + date below.
export function PostCard({ post, dateLabel }: { post: BlogMeta; dateLabel: string }): JSX.Element {
  const { image, gradient, Icon } = getCardVisual(post);
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden rounded-lg border border-white/[0.06]">
        {image ? (
          <img
            src={resolveAssetUrl(image)}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full scale-110 object-cover blur-[14px]"
          />
        ) : (
          <div className="absolute inset-0" style={{ background: gradient }} />
        )}
        <div className="absolute inset-0 bg-black/35" />
        <div className="agb-noise" aria-hidden="true" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon
            className="h-10 w-10 text-white/90 drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)] transition-transform duration-300 motion-safe:group-hover:scale-110"
            strokeWidth={1.5}
            aria-hidden="true"
          />
        </div>
      </div>
      <div className="mt-3">
        <h3 className="text-[15px] font-medium leading-[1.35] text-primary">{post.title}</h3>
        <p className="mt-1 font-mono text-[12px] text-muted">{dateLabel}</p>
      </div>
    </Link>
  );
}
